/*
  BLOOMZ reviews — <bz-reviews>
  Reads genuine reviews from the inline <script type="application/json" data-bz-reviews> (product metafield bloomz.reviews)
  and computes average, count, star distribution and attribute summaries. Nothing is hardcoded.
  Public API / test hook: element.refresh() re-reads the inline JSON and re-renders everything.
*/
(() => {
  if (customElements.get('bz-reviews')) return;

  const STAR_FULL = 'M9 1.2l2.41 4.89 5.39.78-3.9 3.8.92 5.37L9 13.5l-4.82 2.54.92-5.37-3.9-3.8 5.39-.78z';
  const STAR_EMPTY = 'M9 2.9l1.91 3.87 4.27.62-3.09 3.01.73 4.25L9 12.64l-3.82 2.01.73-4.25L2.82 7.39l4.27-.62z';
  const SVG_NS = 'http://www.w3.org/2000/svg';

  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  };

  const starSvg = (full) => {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'bz-stars__icon');
    svg.setAttribute('viewBox', '0 0 18 18');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', full ? STAR_FULL : STAR_EMPTY);
    if (full) path.setAttribute('fill', 'currentColor');
    else {
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.1');
    }
    svg.appendChild(path);
    return svg;
  };

  const setStars = (node, value) => {
    const half = Math.round(value * 2);
    node.style.setProperty('--bz-full', String(Math.floor(half / 2)));
    node.style.setProperty('--bz-half', String(half % 2));
  };

  const buildStars = (value, label) => {
    const wrap = el('span', 'bz-stars');
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', label);
    const off = el('span', 'bz-stars__row');
    const on = el('span', 'bz-stars__row bz-stars__row--on');
    for (let i = 0; i < 5; i += 1) {
      off.appendChild(starSvg(false));
      on.appendChild(starSvg(true));
    }
    wrap.append(off, on);
    setStars(wrap, value);
    return wrap;
  };

  const safeJson = (node, fallback) => {
    if (!node) return fallback;
    try {
      const parsed = JSON.parse(node.textContent || 'null');
      return parsed == null ? fallback : parsed;
    } catch (e) {
      return fallback;
    }
  };

  const httpsUrl = (value) => {
    if (typeof value !== 'string') return null;
    const v = value.trim();
    if (/^https:\/\//i.test(v)) return v;
    if (/^\/\//.test(v)) return 'https:' + v;
    return null;
  };

  const parseDate = (value) => {
    if (typeof value !== 'string') return null;
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]), iso: `${m[1]}-${m[2]}-${m[3]}`, sort: Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]) };
  };

  const parseAttributes = (text) =>
    String(text || '')
      .split(/\r?\n/)
      .map((line) => {
        const at = line.indexOf(':');
        if (at < 1) return null;
        const name = line.slice(0, at).trim();
        const options = line
          .slice(at + 1)
          .split('|')
          .map((o) => o.trim())
          .filter(Boolean);
        return name && options.length > 1 ? { name, options } : null;
      })
      .filter(Boolean);

  class BzReviews extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      if (this.hasAttribute('data-native-off')) return;

      this.cfg = safeJson(this.querySelector('[data-bz-config]'), {});
      this.attrDefs = parseAttributes(this.cfg.attributes);
      this.perPage = Math.max(1, Number(this.dataset.perPage) || 5);
      this.state = { sort: 'newest', star: 0, photos: false, query: '', shown: this.perPage };
      this.refs = {
        head: this.querySelector('[data-head]'),
        score: this.querySelector('[data-score]'),
        headStars: this.querySelector('[data-head-stars]'),
        count: this.querySelector('[data-count]'),
        empty: this.querySelector('[data-empty]'),
        leadAttr: this.querySelector('[data-lead-attr]'),
        breakdown: this.querySelector('[data-breakdown]'),
        based: this.querySelector('[data-based]'),
        dist: this.querySelector('[data-dist]'),
        attrs: this.querySelector('[data-attrs]'),
        tools: this.querySelector('[data-tools]'),
        list: this.querySelector('[data-list]'),
        nomatch: this.querySelector('[data-nomatch]'),
        moreWrap: this.querySelector('[data-more-wrap]'),
        more: this.querySelector('[data-more]'),
        search: this.querySelector('[data-search]'),
        starFilter: this.querySelector('[data-star-filter]'),
        photosFilter: this.querySelector('[data-photos-filter]'),
        sort: this.querySelector('[data-sort]'),
        lightbox: this.querySelector('[data-lightbox]'),
        lightboxImg: this.querySelector('[data-lightbox-img]'),
        writeDialog: this.querySelector('[data-write-dialog]'),
      };

      this.bindToolbar();
      this.bindLightbox();
      this.bindForm();
      this.refresh();
      this.handlePostState();
    }

    /* ---------- data ---------- */
    readReviews() {
      const normalise = (raw, sample) => {
        const list = raw && Array.isArray(raw.reviews) ? raw.reviews : Array.isArray(raw) ? raw : [];
        return list
          .map((r, i) => {
            if (!r || typeof r !== 'object') return null;
            const rating = Math.round(Number(r.rating));
            if (!(rating >= 1 && rating <= 5)) return null;
            const photos = (Array.isArray(r.photos) ? r.photos : []).map(httpsUrl).filter(Boolean);
            const attributes = r.attributes && typeof r.attributes === 'object' && !Array.isArray(r.attributes) ? r.attributes : {};
            return {
              id: String(r.id != null ? r.id : i),
              order: i,
              name: String(r.name || '').trim(),
              verified: r.verified === true,
              date: parseDate(r.date),
              rating,
              title: String(r.title || '').trim(),
              body: String(r.body || '').trim(),
              attributes,
              photos,
              reply: String(r.reply || '').trim(),
              sample: sample === true,
            };
          })
          .filter(Boolean);
      };
      let reviews = normalise(safeJson(this.querySelector('[data-bz-reviews]'), { reviews: [] }), false);
      // Editor-only placeholders: the node exists only when Liquid rendered it inside request.design_mode.
      const sampleNode = this.querySelector('[data-bz-reviews-sample]');
      if (!reviews.length && sampleNode) reviews = normalise(safeJson(sampleNode, { reviews: [] }), true);
      return reviews;
    }

    attrIndex(def, value) {
      if (value == null || value === '') return -1;
      const text = String(value).trim().toLowerCase();
      const byText = def.options.findIndex((o) => o.toLowerCase() === text);
      if (byText > -1) return byText;
      const n = Number(value);
      if (Number.isFinite(n) && n >= 1) return Math.min(def.options.length - 1, Math.round(n) - 1);
      return -1;
    }

    compute(reviews) {
      const count = reviews.length;
      const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      let sum = 0;
      reviews.forEach((r) => {
        dist[r.rating] += 1;
        sum += r.rating;
      });
      const average = count ? Math.round((sum / count) * 10) / 10 : 0;
      const attrs = this.attrDefs
        .map((def) => {
          const idx = reviews.map((r) => this.attrIndex(def, this.lookupAttr(r.attributes, def.name))).filter((i) => i > -1);
          if (!idx.length) return null;
          const mean = idx.reduce((a, b) => a + b, 0) / idx.length;
          return { def, n: idx.length, pos: mean / (def.options.length - 1), label: def.options[Math.round(mean)] };
        })
        .filter(Boolean);
      return { count, average, dist, attrs };
    }

    lookupAttr(attributes, name) {
      const key = Object.keys(attributes).find((k) => k.trim().toLowerCase() === name.toLowerCase());
      return key ? attributes[key] : null;
    }

    /* ---------- public ---------- */
    refresh() {
      if (!this.refs) return;
      this.reviews = this.readReviews();
      this.summary = this.compute(this.reviews);
      this.state.shown = this.perPage;
      this.renderHead();
      this.renderList();
    }

    /* ---------- header ---------- */
    starsLabel(n) {
      return String(this.cfg.starsLabel || '[n] / 5').replace('[n]', String(n));
    }

    renderHead() {
      const { refs, summary } = this;
      let count = summary.count;
      let average = summary.average;
      const appCount = Number(this.dataset.appCount) || 0;
      const appRating = Number(this.dataset.appRating) || 0;
      const fromApp = !count && appCount > 0 && appRating > 0;
      if (fromApp) {
        count = appCount;
        average = Math.round(appRating * 10) / 10;
      }
      const has = count > 0;
      if (refs.head) refs.head.classList.toggle('is-empty', !has);
      if (refs.score) {
        refs.score.hidden = !has;
        refs.score.textContent = has ? `${average}${this.cfg.scoreSuffix || ''}` : '';
      }
      if (refs.headStars) {
        setStars(refs.headStars, has ? average : 0);
        refs.headStars.setAttribute('aria-label', this.starsLabel(has ? average : 0));
      }
      if (refs.count) {
        refs.count.hidden = !has;
        refs.count.textContent = has ? `${count} ${count === 1 ? this.cfg.reviewOne || '' : this.cfg.reviewMany || ''}` : '';
      }
      if (refs.empty) refs.empty.hidden = has;

      const own = summary.count > 0;
      if (refs.breakdown) refs.breakdown.hidden = !own || !(refs.dist || (refs.attrs && summary.attrs.length > 1));
      if (refs.head && !own) refs.head.classList.toggle('is-empty', true);
      if (refs.based) refs.based.textContent = String(this.cfg.basedOn || '').replace('[count]', String(summary.count));
      if (refs.tools) refs.tools.hidden = !own;

      if (refs.leadAttr) {
        refs.leadAttr.replaceChildren();
        const lead = own ? summary.attrs[0] : null;
        refs.leadAttr.hidden = !lead;
        if (lead) refs.leadAttr.appendChild(this.buildRange(lead, false));
      }
      if (refs.attrs) {
        refs.attrs.replaceChildren();
        const rest = own ? summary.attrs.slice(refs.leadAttr ? 1 : 0) : [];
        rest.forEach((a) => refs.attrs.appendChild(this.buildRange(a, true)));
        refs.attrs.hidden = !rest.length;
      }
      if (refs.dist) {
        refs.dist.replaceChildren();
        if (own) {
          for (let n = 5; n >= 1; n -= 1) {
            const row = el('button', 'bz-rv-dist__row');
            row.type = 'button';
            row.dataset.distStar = String(n);
            row.setAttribute('aria-pressed', String(this.state.star === n));
            row.setAttribute('aria-label', `${this.starsLabel(n)}: ${summary.dist[n]}`);
            const label = el('span', 'bz-rv-dist__label', String(n));
            label.appendChild(starSvg(true));
            const bar = el('span', 'bz-rv-dist__bar');
            const fill = el('span', 'bz-rv-dist__fill');
            fill.style.setProperty('--bz-pct', String(summary.dist[n] / summary.count));
            bar.appendChild(fill);
            row.append(label, bar, el('span', 'bz-rv-dist__n', String(summary.dist[n])));
            row.addEventListener('click', () => this.setStar(this.state.star === n ? 0 : n));
            refs.dist.appendChild(row);
          }
        }
      }
    }

    buildRange(attr, withEnds) {
      const row = el('div', 'bz-rv-range');
      row.append(el('span', 'bz-rv-range__name', attr.def.name), el('span', 'bz-rv-range__value', attr.label));
      const bar = el('span', 'bz-rv-range__bar');
      bar.setAttribute('role', 'img');
      bar.setAttribute('aria-label', `${attr.def.name}: ${attr.label} (${attr.def.options[0]} – ${attr.def.options[attr.def.options.length - 1]})`);
      const mark = el('span', 'bz-rv-range__mark');
      mark.style.setProperty('--bz-pos', String(Math.max(0, Math.min(1, attr.pos))));
      bar.appendChild(mark);
      row.appendChild(bar);
      if (withEnds) {
        const ends = el('span', 'bz-rv-range__ends');
        ends.setAttribute('aria-hidden', 'true');
        ends.append(el('span', '', attr.def.options[0]), el('span', '', attr.def.options[attr.def.options.length - 1]));
        row.appendChild(ends);
      }
      return row;
    }

    /* ---------- list ---------- */
    filtered() {
      const { state } = this;
      const q = state.query.trim().toLowerCase();
      let list = this.reviews.filter((r) => {
        if (state.star && r.rating !== state.star) return false;
        if (state.photos && !r.photos.length) return false;
        if (q) {
          const hay = `${r.title} ${r.body} ${r.name}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });
      const byDate = (a, b) => (b.date ? b.date.sort : 0) - (a.date ? a.date.sort : 0) || b.order - a.order;
      const sorters = {
        newest: byDate,
        highest: (a, b) => b.rating - a.rating || byDate(a, b),
        lowest: (a, b) => a.rating - b.rating || byDate(a, b),
        photos: (a, b) => (b.photos.length ? 1 : 0) - (a.photos.length ? 1 : 0) || byDate(a, b),
      };
      list = list.slice().sort(sorters[state.sort] || byDate);
      return list;
    }

    formatDate(d) {
      if (!d) return '';
      const p = (n) => String(n).padStart(2, '0');
      const yy = p(d.y % 100);
      if (this.cfg.dateFormat === 'iso') return d.iso;
      if (this.cfg.dateFormat === 'dmy') return `${p(d.d)}/${p(d.m)}/${yy}`;
      return `${p(d.m)}/${p(d.d)}/${yy}`;
    }

    renderList(focusIndex) {
      const { refs, state } = this;
      if (!refs.list) return;
      const all = this.filtered();
      const visible = all.slice(0, state.shown);
      refs.list.replaceChildren(...visible.map((r) => this.buildCard(r)));
      if (refs.nomatch) refs.nomatch.hidden = !(this.reviews.length > 0 && all.length === 0);
      if (refs.moreWrap) refs.moreWrap.hidden = all.length <= state.shown;
      this.dataset.total = String(this.reviews.length);
      this.dataset.matching = String(all.length);
      this.dataset.visible = String(visible.length);
      requestAnimationFrame(() => this.updateClamps());
      if (focusIndex != null && refs.list.children[focusIndex]) {
        const target = refs.list.children[focusIndex];
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    }

    buildCard(r) {
      const cfg = this.cfg;
      const card = el('article', 'bz-rv');
      card.dataset.reviewId = r.id;
      card.dataset.rating = String(r.rating);

      const left = el('div', 'bz-rv__left');
      left.appendChild(buildStars(r.rating, this.starsLabel(r.rating)));
      const author = el('p', 'bz-rv__author');
      if (r.name) author.appendChild(el('span', 'bz-rv__name', r.name));
      if (r.verified && cfg.showVerified && cfg.verified) author.appendChild(el('span', 'bz-rv__verified', cfg.verified));
      if (author.childNodes.length) left.appendChild(author);
      if (r.sample) left.appendChild(el('p', 'bz-rv__sample', 'Sample review – only visible in the theme editor'));

      const main = el('div', 'bz-rv__main');
      if (r.title) main.appendChild(el('h3', 'bz-rv__title', r.title));
      if (r.body) {
        const body = el('p', 'bz-rv__body is-clamped', r.body);
        const more = el('button', 'bz-rv__more', cfg.readMore || '');
        more.type = 'button';
        more.hidden = true;
        more.setAttribute('aria-expanded', 'false');
        more.addEventListener('click', () => {
          const open = body.classList.toggle('is-clamped') === false;
          more.setAttribute('aria-expanded', String(open));
          more.textContent = open ? cfg.readLess || '' : cfg.readMore || '';
        });
        main.append(body, more);
      }
      if (cfg.showAttrLines) {
        const keys = Object.keys(r.attributes).filter((k) => r.attributes[k] != null && String(r.attributes[k]).trim() !== '');
        if (keys.length) {
          const dl = el('dl', 'bz-rv__attrs');
          keys.forEach((k) => {
            const def = this.attrDefs.find((d) => d.name.toLowerCase() === k.trim().toLowerCase());
            let value = String(r.attributes[k]).trim();
            if (def) {
              const idx = this.attrIndex(def, r.attributes[k]);
              if (idx > -1) value = def.options[idx];
            }
            const line = el('div', 'bz-rv__attr');
            line.append(el('dt', '', def ? def.name : k.trim()), el('dd', '', value));
            dl.appendChild(line);
          });
          main.appendChild(dl);
        }
      }
      if (cfg.showPhotos && r.photos.length) {
        const photos = el('div', 'bz-rv__photos');
        r.photos.forEach((url, i) => {
          const btn = el('button', 'bz-rv__photo');
          btn.type = 'button';
          btn.setAttribute('aria-label', `${cfg.photoAlt || ''} ${i + 1}`.trim());
          const img = el('img');
          img.loading = 'lazy';
          img.decoding = 'async';
          img.alt = '';
          img.width = 100;
          img.height = 100;
          img.src = url;
          btn.appendChild(img);
          btn.addEventListener('click', () => this.openLightbox(r.photos, i, btn));
          photos.appendChild(btn);
        });
        main.appendChild(photos);
      }
      if (cfg.showReply && r.reply) {
        const reply = el('div', 'bz-rv__reply');
        if (cfg.replyLabel) reply.appendChild(el('span', 'bz-rv__reply-label', cfg.replyLabel));
        reply.appendChild(el('span', 'bz-rv__reply-text', r.reply));
        main.appendChild(reply);
      }

      const right = el('div', 'bz-rv__right');
      if (cfg.showDate && r.date) {
        const time = el('time', '', this.formatDate(r.date));
        time.dateTime = r.date.iso;
        right.appendChild(time);
      }
      card.append(left, main, right);
      return card;
    }

    updateClamps() {
      this.querySelectorAll('.bz-rv__body.is-clamped').forEach((body) => {
        const more = body.nextElementSibling;
        if (more && more.classList.contains('bz-rv__more')) more.hidden = body.scrollHeight <= body.clientHeight + 1;
      });
    }

    /* ---------- toolbar ---------- */
    setStar(n) {
      this.state.star = n;
      this.state.shown = this.perPage;
      if (this.refs.starFilter) this.refs.starFilter.value = String(n);
      this.querySelectorAll('[data-dist-star]').forEach((row) => row.setAttribute('aria-pressed', String(Number(row.dataset.distStar) === n)));
      this.renderList();
    }

    // Native selects are as wide as their longest option: size the sort select to the chosen one (arrow stays next to the text).
    fitSelect(select) {
      if (window.CSS && CSS.supports && CSS.supports('field-sizing', 'content')) return;
      const option = select.options[select.selectedIndex];
      if (!option) return;
      const probe = el('span', '', option.textContent);
      const cs = getComputedStyle(select);
      probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;left:-9999px;top:0;';
      probe.style.font = cs.font;
      probe.style.letterSpacing = cs.letterSpacing;
      probe.style.textTransform = cs.textTransform;
      select.parentNode.appendChild(probe);
      select.style.width = Math.ceil(probe.getBoundingClientRect().width) + 18 + 'px';
      probe.remove();
    }

    bindToolbar() {
      const { refs } = this;
      if (refs.sort) {
        this.fitSelect(refs.sort);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.fitSelect(refs.sort));
        refs.sort.addEventListener('change', () => {
          this.fitSelect(refs.sort);
          this.state.sort = refs.sort.value;
          this.state.shown = this.perPage;
          this.renderList();
        });
      }
      if (refs.starFilter) refs.starFilter.addEventListener('change', () => this.setStar(Number(refs.starFilter.value) || 0));
      if (refs.photosFilter) {
        refs.photosFilter.addEventListener('change', () => {
          this.state.photos = refs.photosFilter.checked;
          this.state.shown = this.perPage;
          this.renderList();
        });
      }
      if (refs.search) {
        let timer;
        refs.search.addEventListener('input', () => {
          clearTimeout(timer);
          timer = setTimeout(() => {
            this.state.query = refs.search.value;
            this.state.shown = this.perPage;
            this.renderList();
          }, 150);
        });
      }
      if (refs.more) {
        refs.more.addEventListener('click', () => {
          const first = this.state.shown;
          this.state.shown += this.perPage;
          this.renderList(first);
        });
      }
      if (refs.count) {
        refs.count.addEventListener('click', (e) => {
          e.preventDefault();
          refs.list.scrollIntoView({ behavior: 'smooth', block: 'start' });
          refs.list.focus({ preventScroll: true });
        });
      }
      window.addEventListener('resize', () => {
        clearTimeout(this._rz);
        this._rz = setTimeout(() => this.updateClamps(), 200);
      });
    }

    /* ---------- lightbox ---------- */
    bindLightbox() {
      const box = this.refs.lightbox;
      if (!box) return;
      box.querySelector('[data-lightbox-close]').addEventListener('click', () => box.close());
      box.querySelector('[data-lightbox-prev]').addEventListener('click', () => this.stepLightbox(-1));
      box.querySelector('[data-lightbox-next]').addEventListener('click', () => this.stepLightbox(1));
      box.addEventListener('click', (e) => {
        if (e.target === box) box.close();
      });
      box.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.stepLightbox(-1);
        if (e.key === 'ArrowRight') this.stepLightbox(1);
      });
      box.addEventListener('close', () => {
        if (this._lbOpener && this._lbOpener.isConnected) this._lbOpener.focus({ preventScroll: true });
        this._lbOpener = null;
      });
    }

    openLightbox(photos, index, opener) {
      const box = this.refs.lightbox;
      if (!box || typeof box.showModal !== 'function') return;
      this._lbPhotos = photos;
      this._lbIndex = index;
      this._lbOpener = opener;
      this.showLightboxPhoto();
      if (!box.open) box.showModal();
    }

    stepLightbox(delta) {
      if (!this._lbPhotos || this._lbPhotos.length < 2) return;
      this._lbIndex = (this._lbIndex + delta + this._lbPhotos.length) % this._lbPhotos.length;
      this.showLightboxPhoto();
    }

    showLightboxPhoto() {
      const box = this.refs.lightbox;
      this.refs.lightboxImg.src = this._lbPhotos[this._lbIndex];
      const many = this._lbPhotos.length > 1;
      box.querySelector('[data-lightbox-prev]').hidden = !many;
      box.querySelector('[data-lightbox-next]').hidden = !many;
    }

    /* ---------- write a review ---------- */
    bindForm() {
      const dialog = this.refs.writeDialog;
      const opener = this.querySelector('[data-write-open]');
      if (!dialog || !opener) return;
      opener.addEventListener('click', () => {
        if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
      });
      dialog.querySelector('[data-write-close]').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => opener.focus({ preventScroll: true }));

      const form = dialog.querySelector('form');
      if (!form) return;
      const rate = form.querySelector('[data-rate]');
      const paint = (value) => {
        if (!rate) return;
        rate.querySelectorAll('[data-rate-star]').forEach((star) => star.classList.toggle('is-on', Number(star.dataset.rateStar) <= value));
      };
      const current = () => {
        const checked = form.querySelector('input[name="contact[rating]"]:checked');
        return checked ? Number(checked.value) : 0;
      };
      if (rate) {
        rate.addEventListener('change', () => {
          paint(current());
          rate.removeAttribute('aria-invalid');
        });
        rate.querySelectorAll('[data-rate-star]').forEach((star) => {
          star.addEventListener('mouseenter', () => paint(Number(star.dataset.rateStar)));
          star.addEventListener('mouseleave', () => paint(current()));
        });
      }
      form.addEventListener('input', (e) => {
        if (e.target.matches('[aria-invalid]') && e.target.checkValidity()) e.target.removeAttribute('aria-invalid');
      });
      const validate = () => {
        const error = form.querySelector('[data-form-error]');
        const invalid = [...form.elements].filter((f) => f.willValidate && !f.checkValidity());
        form.querySelectorAll('[aria-invalid]').forEach((f) => f.removeAttribute('aria-invalid'));
        if (error) error.hidden = invalid.length === 0;
        if (!invalid.length) return true;
        invalid.forEach((f) => {
          if (f.type === 'radio') rate && rate.setAttribute('aria-invalid', 'true');
          else f.setAttribute('aria-invalid', 'true');
        });
        invalid[0].focus();
        return false;
      };
      // Validate on the button click (Enter in a field also fires it) so that nothing else listening to
      // "submit" (Shopify's bot protection re-submits contact forms programmatically) can send an invalid form.
      const send = form.querySelector('[type="submit"]');
      if (send) {
        send.addEventListener('click', (e) => {
          if (!validate()) e.preventDefault();
        });
      }
      form.addEventListener(
        'submit',
        (e) => {
          if (validate()) return;
          e.preventDefault();
          e.stopImmediatePropagation();
        },
        true
      );
    }

    handlePostState() {
      const dialog = this.refs.writeDialog;
      if (!dialog) return;
      const success = dialog.querySelector('[data-form-success]');
      const serverError = dialog.querySelector('[data-form-server-error]');
      if (success) {
        const slot = this.querySelector('[data-form-status-slot]');
        if (slot) slot.appendChild(success);
        this.scrollIntoView({ block: 'start' });
        success.focus({ preventScroll: true });
      } else if (serverError && typeof dialog.showModal === 'function') {
        this.scrollIntoView({ block: 'start' });
        dialog.showModal();
        serverError.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('bz-reviews', BzReviews);

  // PDP stars link (rendered by bz-main-product only when there are real reviews): smooth scroll + focus.
  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest('[data-bz-reviews-link]');
    if (!link) return;
    const target = document.getElementById('bz-reviews');
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    target.focus({ preventScroll: true });
  });
})();
